"use client";

import type React from "react";
import Image from "next/image";

import { cn } from "@rivval/css";
import { Button } from "@rivval/ui/button";
import { GlitchText } from "@rivval/ui/glitch-text";

import { env } from "~/env.mjs";

interface ArticleProps {
  title: string;
  date: string;
  image: string;
  content: React.ReactNode;
  downloadLink?: string;
  discussionLink?: string;
  backgroundColor?: string;
}

export function FeaturedArticle({
  title,
  date,
  image,
  content,
  discussionLink,
  backgroundColor = "bg-transparent",
}: ArticleProps) {
  return (
    <article className="mx-auto max-w-(--breakpoint-lg)">
      <div className={cn("container mx-auto px-4 py-20", backgroundColor)}>
        <header className="relative mb-12">
          <div className="article-image-container relative aspect-16/9">
            <div
              className="absolute inset-0 z-10"
              style={{
                backgroundImage: `url(${env.NEXT_PUBLIC_ASSETS_URL}/images/storefront/border/screen.png)`,
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                pointerEvents: "none",
                opacity: 0.9,
              }}
            />

            <div className="absolute inset-0 p-8">
              <div className="article-image-layer" />

              <div className="article-image relative h-full overflow-hidden">
                <Image
                  src={image}
                  alt={title}
                  className="h-full w-full -rotate-2 transform object-cover"
                  fill
                  sizes="(max-width: 1024px) 100vw, 1024px"
                />

                <div className="article-image-glitch">
                  <div
                    className="article-image-glitch-effect"
                    style={{ backgroundImage: `url(${image})` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="relative">
          <div className="mb-4 flex items-center justify-between border-b border-yellow-400/30"></div>

          <div className="relative z-10">
            <div className="bg-yellow-400">
              <h1 className="razor-underline px-8 py-4 text-xl font-bold text-black md:text-4xl">
                <GlitchText text={title} glitchDuration={1500} />
              </h1>
              <div className="flex items-center justify-between border-b border-yellow-400/30 pb-4">
                <time className="px-8 py-4 text-red-800" dateTime={date}>
                  {date}
                </time>
              </div>
            </div>

            <div className="razor-top razor-bottom max-w-none bg-black p-8">
              {content}
            </div>

            {discussionLink && (
              <div className="mt-12 border-t border-yellow-400/30 pt-8">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">comments_16</span>
                  <Button
                    className="relative border border-yellow-400 bg-black text-yellow-400 hover:bg-yellow-400 hover:text-black"
                    asChild
                  >
                    <a
                      href={discussionLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      join the discussion
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
